import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client, APIError } from "@/api"
import type { StatementCadence, StatementStatus } from "@/lib/affiliateStatements"

// Shapes mirror backend/src/lib/affiliate-statements/serialize.ts. Money is in cents.
export interface AffiliateStatement {
  public_id: string
  period_start: string
  period_end: string
  status: StatementStatus
  commission_percent: number
  gross_cents: number
  tax_cents: number
  fee_cents: number
  refund_cents: number
  net_revenue_cents: number
  commission_cents: number
  carried_balance_cents: number
  amount_due_cents: number
  generated_at: string
  sent_at: string | null
  created_at: string
  updated_at: string
  payment_public_id: string | null
  payment_due_at: string
  // False while the period is still running; such a statement can't be sent.
  period_complete: boolean
  // Stored from the last generation: invoices that couldn't be matched to one team, etc.
  warnings: string[]
  // Recipients the last send didn't reach; empty when it reached everyone.
  send_failures: { email: string; error: string }[]
}

// The one period a statement can be generated for next, so statements stay in order.
export interface NextStatementPeriod {
  period_start: string
  period_end: string
  cadence: StatementCadence
  // The period already has a draft: regenerate it from the statement page instead.
  has_draft: boolean
  period_complete: boolean
}

export interface AffiliateStatementList {
  statements: AffiliateStatement[]
  next_period: NextStatementPeriod | null
}

export interface AffiliateStatementLine {
  kind: "payment" | "refund"
  team_public_id: string
  team_name: string
  stripe_invoice_id: string
  stripe_refund_id: string | null
  occurred_at: string
  first_year_ends_at: string
  gross_cents: number
  tax_cents: number
  fee_cents: number
  net_cents: number
  commission_cents: number
}

export interface AffiliateStatementPayment {
  public_id: string
  amount_cents: number
  method: string
  reference: string | null
  admin_notes: string | null
  paid_at: string
}

interface StatementUser {
  public_id: string
  name: string | null
}

export interface AffiliateStatementDetail {
  statement: AffiliateStatement
  affiliate: { public_id: string; name: string; ref_code: string }
  lines: AffiliateStatementLine[]
  recipients: string[]
  download_url: string
  generated_by_user: StatementUser | null
  sent_by_user: StatementUser | null
  payment: AffiliateStatementPayment | null
}

export interface GenerateStatementResult extends AffiliateStatementDetail {
  regenerated: boolean
  warnings: string[]
}

export interface SendStatementResult extends AffiliateStatementDetail {
  delivered_to: string[]
  failed: { email: string; error: string }[]
}

interface ErrorBody {
  code?: string
  message?: string
}

async function failure(
  res: { status: number; clone: () => { json: () => Promise<unknown> } },
  code: string,
  fallback: string
): Promise<APIError> {
  const err = (await res.clone().json().catch(() => ({}))) as ErrorBody
  return new APIError(res.status, err.code || code, new Error(err.message || fallback))
}

// `cadence` only matters for an affiliate whose own cadence is "none" and has no statements yet.
export function useAffiliateStatements(
  affiliatePublicId: string,
  enabled = true,
  cadence?: StatementCadence
) {
  return useQuery<AffiliateStatementList>({
    queryKey: ["affiliate-statements", affiliatePublicId, cadence ?? null],
    queryFn: async () => {
      const res = await client.admin.affiliates[":publicId"].statements.$get({
        param: { publicId: affiliatePublicId },
        query: cadence ? { cadence } : {},
      })
      if (!res.ok) throw new Error("Failed to fetch statements")
      return res.json() as Promise<AffiliateStatementList>
    },
    enabled: !!affiliatePublicId && enabled,
  })
}

export function useGenerateStatement() {
  const qc = useQueryClient()
  return useMutation<
    GenerateStatementResult,
    APIError,
    { affiliatePublicId: string; periodStart: string; cadence?: StatementCadence }
  >({
    mutationFn: async ({ affiliatePublicId, ...json }) => {
      const res = await client.admin.affiliates[":publicId"].statements.$post({
        param: { publicId: affiliatePublicId },
        json,
      })
      if (!res.ok) throw await failure(res, "STATEMENT_GENERATE_FAILED", "Failed to generate statement")
      return (await res.json()) as GenerateStatementResult
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ["affiliate-statements", variables.affiliatePublicId] })
      qc.invalidateQueries({ queryKey: ["affiliate-statement", data.statement.public_id] })
      qc.invalidateQueries({ queryKey: ["affiliate-statement-preview", data.statement.public_id] })
    },
  })
}

export function useAffiliateStatement(statementPublicId: string) {
  return useQuery<AffiliateStatementDetail>({
    queryKey: ["affiliate-statement", statementPublicId],
    queryFn: async () => {
      const res = await client.admin["affiliate-statements"][":publicId"].$get({
        param: { publicId: statementPublicId },
      })
      if (!res.ok) throw new Error("Failed to fetch statement")
      return res.json() as Promise<AffiliateStatementDetail>
    },
    enabled: !!statementPublicId,
  })
}

export interface StatementEmailPreview {
  subject: string
  html: string
  text: string
  recipients: string[]
}

// The email a send would deliver. Keyed on the statement so a regenerate/send refetches it.
export function useStatementEmailPreview(statementPublicId: string) {
  return useQuery<StatementEmailPreview>({
    queryKey: ["affiliate-statement-preview", statementPublicId],
    queryFn: async () => {
      const res = await client.admin["affiliate-statements"][":publicId"]["email-preview"].$get({
        param: { publicId: statementPublicId },
      })
      if (!res.ok) throw new Error("Failed to render the email preview")
      return res.json() as Promise<StatementEmailPreview>
    },
    enabled: !!statementPublicId,
  })
}

// After a send or mark-paid the affiliate's owed total and statement list change too.
function invalidateStatement(qc: ReturnType<typeof useQueryClient>, statementPublicId: string) {
  qc.invalidateQueries({ queryKey: ["affiliate-statement", statementPublicId] })
  qc.invalidateQueries({ queryKey: ["affiliate-statement-preview", statementPublicId] })
  qc.invalidateQueries({ queryKey: ["affiliate-statements"] })
  qc.invalidateQueries({ queryKey: ["affiliate"] })
  qc.invalidateQueries({ queryKey: ["affiliates"] })
}

export function useSendStatement() {
  const qc = useQueryClient()
  return useMutation<SendStatementResult, APIError, string>({
    mutationFn: async (statementPublicId) => {
      const res = await client.admin["affiliate-statements"][":publicId"].send.$post({
        param: { publicId: statementPublicId },
      })
      if (!res.ok) throw await failure(res, "STATEMENT_SEND_FAILED", "Failed to send statement")
      return (await res.json()) as SendStatementResult
    },
    onSuccess: (_data, statementPublicId) => invalidateStatement(qc, statementPublicId),
  })
}

export type MarkStatementPaidPayload = {
  statementPublicId: string
  method: "check" | "ach" | "paypal" | "wire" | "other"
  reference?: string
  adminNotes?: string
}

export function useMarkStatementPaid() {
  const qc = useQueryClient()
  return useMutation<
    { statement: AffiliateStatement; payment: AffiliateStatementPayment | null },
    APIError,
    MarkStatementPaidPayload
  >({
    mutationFn: async ({ statementPublicId, ...json }) => {
      const res = await client.admin["affiliate-statements"][":publicId"]["mark-paid"].$post({
        param: { publicId: statementPublicId },
        json,
      })
      if (!res.ok) throw await failure(res, "STATEMENT_MARK_PAID_FAILED", "Failed to mark statement paid")
      return (await res.json()) as {
        statement: AffiliateStatement
        payment: AffiliateStatementPayment | null
      }
    },
    onSuccess: (_data, variables) => invalidateStatement(qc, variables.statementPublicId),
  })
}

// The signed-in admin's CSV download (the emailed link is the public, token-keyed one).
export async function getStatementCsv(statementPublicId: string): Promise<Blob> {
  const res = await client.admin["affiliate-statements"][":publicId"].csv.$get({
    param: { publicId: statementPublicId },
  })
  if (!res.ok) throw new Error(`Failed to download the CSV: ${res.status}`)
  return res.blob()
}

export interface StatementNeedingAttention {
  statement_public_id: string
  affiliate: { public_id: string; name: string }
  period_start: string
  period_end: string
  kind: "ready_to_send" | "payment_due" | "payment_overdue"
  amount_due_cents: number
  payment_due_at: string
  has_warnings: boolean
}

// Fetched when the dashboard opens and not polled or refetched on focus.
export function useStatementsNeedingAttention() {
  return useQuery<{ items: StatementNeedingAttention[] }>({
    queryKey: ["affiliate-statements-attention"],
    queryFn: async () => {
      const res = await client.admin["affiliate-statements"].attention.$get()
      if (!res.ok) throw new Error("Failed to fetch statements needing attention")
      return res.json() as Promise<{ items: StatementNeedingAttention[] }>
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
  })
}
